/**
 * How detections are drawn on the video, chosen by the viewer and kept for next time: the colour of a one-kind
 * video's marks (a video counting several kinds keeps a colour per kind), as "r, g, b". Green is the default; the
 * other presets stay readable on snow, grass, water and sky, and none is the red of a proposed removal or the white
 * of a picked mark. The viewer may also pick a colour of their own.
 */
export const DETECTION_COLORS = [
	{ name: 'green', rgb: '32, 172, 107' },
	{ name: 'yellow', rgb: '255, 214, 10' },
	{ name: 'cyan', rgb: '34, 211, 238' },
	{ name: 'magenta', rgb: '236, 72, 153' },
	{ name: 'orange', rgb: '251, 146, 60' },
];

const KEY = 'videoDetectionColor';
const RGB = /^\d{1,3}, \d{1,3}, \d{1,3}$/;

export const colorNamed = name =>
	DETECTION_COLORS.find(c => c.name === name) || DETECTION_COLORS[0];

const isRgb = rgb => RGB.test(rgb || '') && rgb.split(',').every(v => Number(v) <= 255);

/** The colour drawn as `rgb` ("r, g, b"): a preset, by name, or the viewer's own; green for anything else. */
export const colorOf = rgb =>
	DETECTION_COLORS.find(c => c.rgb === rgb) ||
	(isRgb(rgb) ? { name: 'custom', rgb } : DETECTION_COLORS[0]);

/** "r, g, b" as the hex the colour picker takes. */
export const toHex = rgb =>
	`#${rgb
		.split(',')
		.map(v => Number(v).toString(16).padStart(2, '0'))
		.join('')}`;

/** The colour picker's { r, g, b } as "r, g, b". */
export const fromRgb = ({ r, g, b }) => [r, g, b].map(v => Math.round(v)).join(', ');

/** The kept colour: saved as "r, g, b", or by its preset name by an earlier version of the page. */
export const savedColor = () => {
	try {
		const kept = localStorage.getItem(KEY);
		return isRgb(kept) ? colorOf(kept) : colorNamed(kept);
	} catch {
		return DETECTION_COLORS[0];
	}
};

export const saveColor = rgb => {
	try {
		localStorage.setItem(KEY, rgb);
	} catch {
		// a private window: the choice lasts this visit only
	}
};

/**
 * A box's corners as one path: an arm on each corner, a quarter of the shorter side (at least minArm, at most maxArm),
 * so a mark frames the object without hiding it.
 */
export const cornerPath = (path, x1, y1, w, h, minArm, maxArm) => {
	const a = Math.max(minArm, Math.min(maxArm, 0.25 * Math.min(w, h)));
	const x2 = x1 + w;
	const y2 = y1 + h;
	path.moveTo(x1, y1 + a);
	path.lineTo(x1, y1);
	path.lineTo(x1 + a, y1);
	path.moveTo(x2 - a, y1);
	path.lineTo(x2, y1);
	path.lineTo(x2, y1 + a);
	path.moveTo(x2, y2 - a);
	path.lineTo(x2, y2);
	path.lineTo(x2 - a, y2);
	path.moveTo(x1 + a, y2);
	path.lineTo(x1, y2);
	path.lineTo(x1, y2 - a);
	return path;
};
